import { useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import {
  Avatar,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/slider/sliderSlice';
import { findSlider, createSlider, patchSlider } from '../../store/slider/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import { omit } from 'lodash';
import SliderFormModal from './SliderFormModal';
import {
  bannerPlacementLabel,
  getBannerPlacement,
  IInitialState,
  Slider,
} from '../../store/slider/api/interface';
import sliderSchema from '../../validation/sliderSchema';
import CloseIcon from '@mui/icons-material/Close';
import { getAssetURl } from '../../utils/url';
import { ActionType, Language } from '../../enum';

const toBool = (value: boolean | string | undefined): boolean =>
  value === true || String(value) === 'true';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    data,
    status,
    isLoading,
    totalRecords,
    limit,
    currentPage,
    skip,
  } = useAppSelector((state) => state.slider);
  const { toggle, onOpen, onClose } = useToggle();
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const [bannerFilter, setBannerFilter] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (status === 'idle') {
      const query: Record<string, any> = { limit, skip };
      if (bannerFilter) {
        query.bannerPlacement = bannerFilter;
      }
      if (actionTypeFilter) {
        query.actionType = actionTypeFilter;
      }
      if (languageFilter) {
        query.language = languageFilter;
      }
      if (statusFilter === 'true' || statusFilter === 'false') {
        query.activeState = statusFilter === 'true';
      }
      dispatch(findSlider(query));
    }
  }, [dispatch, status]);

  useEffect(() => {
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
    dispatch(setStatus({ status: 'idle' }));
  }, [bannerFilter, actionTypeFilter, languageFilter, statusFilter, dispatch]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik<IInitialState>({
    initialValues: {
      id: '',
      actionType: '',
      actionId: '',
      position: '',
      imageUrl: '',
      language: '',
      activeState: true,
      isAdvertiseBanner: false,
      isHomePageBottomImage: false,
      isCakeBanner: false,
      isFlowerBanner: false,
      isInviteEarnBanner: false,
      isEventServiceBanner: false,
    },
    validationSchema: sliderSchema,
    onSubmit: async (formValues) => {
      try {
        const payload = { ...formValues };
        if (
          payload.actionType === ActionType.makeACake ||
          payload.actionType === ActionType.noAction ||
          payload.actionType === ActionType.reward ||
          payload.actionType === ActionType.flower ||
          payload.actionType === ActionType.eventService ||
          !payload.actionType
        ) {
          delete payload.actionId;
        }

        if (payload.id) {
          await dispatch(patchSlider(payload)).unwrap();
        } else {
          const reqBody = omit(payload, ['id']);
          await dispatch(createSlider(reqBody)).unwrap();
        }

        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: Slider): void => {
    onOpen();
    setValues({
      id: value.id,
      actionType: value?.actionType || '',
      actionId: value?.actionId || '',
      position: String(value?.position ?? ''),
      imageUrl: value?.imageUrl || '',
      language: value?.language || '',
      activeState: value?.activeState,
      isAdvertiseBanner: toBool(value?.isAdvertiseBanner),
      isHomePageBottomImage: toBool(value?.isHomePageBottomImage),
      isCakeBanner: toBool(value?.isCakeBanner),
      isFlowerBanner: toBool(value?.isFlowerBanner),
      isInviteEarnBanner: toBool(value?.isInviteEarnBanner),
      isEventServiceBanner: toBool(value?.isEventServiceBanner),
    });
  };

  const handleOpenCreate = (): void => {
    resetForm();
    onOpen();
  };

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [imageState, setImageState] = useState<string>();

  const handleOpenLightBox = (imageObject: string) => {
    setOpenLightBox(true);
    setImageState(imageObject);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
    setImageState('');
  };

  return (
    <>
      <Page title={'Slider & Banner: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Slider & Banner'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={handleOpenCreate}
              >
                {'New Slider / Banner'}
              </Button>
            }
          />

          <Card>
            <Grid
              container
              spacing={2}
              alignItems='center'
              sx={{ py: 2.5, px: 3 }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size='small'>
                  <InputLabel>Banner</InputLabel>
                  <Select
                    label='Banner'
                    value={bannerFilter}
                    onChange={(e) => setBannerFilter(e.target.value)}
                  >
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value='slider'>Slider</MenuItem>
                    <MenuItem value='advertise'>Advertise Banner</MenuItem>
                    <MenuItem value='homeBottom'>Home Bottom Image</MenuItem>
                    <MenuItem value='cake'>Cake</MenuItem>
                    <MenuItem value='flower'>Flower</MenuItem>
                    <MenuItem value='inviteEarn'>Invite & Earn</MenuItem>
                    <MenuItem value='eventService'>Event Service</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size='small'>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label='Type'
                    value={actionTypeFilter}
                    onChange={(e) => setActionTypeFilter(e.target.value)}
                  >
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value={ActionType.makeACake}>
                      {ActionType.makeACake}
                    </MenuItem>
                    <MenuItem value={ActionType.occasionCategory}>
                      {ActionType.occasionCategory}
                    </MenuItem>
                    <MenuItem value={ActionType.occasionCake}>
                      {ActionType.occasionCake}
                    </MenuItem>
                    <MenuItem value={ActionType.noAction}>
                      {ActionType.noAction}
                    </MenuItem>
                    <MenuItem value={ActionType.reward}>
                      {ActionType.reward}
                    </MenuItem>
                    <MenuItem value={ActionType.flower}>
                      {ActionType.flower}
                    </MenuItem>
                    <MenuItem value={ActionType.eventService}>
                      {ActionType.eventService}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size='small'>
                  <InputLabel>Language</InputLabel>
                  <Select
                    label='Language'
                    value={languageFilter}
                    onChange={(e) => setLanguageFilter(e.target.value)}
                  >
                    <MenuItem value=''>All</MenuItem>
                    <MenuItem value={Language.english}>en</MenuItem>
                    <MenuItem value={Language.arabic}>ar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size='small'>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label='Status'
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value=''>All Status</MenuItem>
                    <MenuItem value='true'>Active</MenuItem>
                    <MenuItem value='false'>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Scrollbar>
              <TableContainer
                sx={{ minWidth: 1000, position: 'relative' }}
              >
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Image'}</TableCell>
                      <TableCell align='center'>{'Position'}</TableCell>
                      <TableCell align='center'>{'Type'}</TableCell>
                      <TableCell align='center'>{'Banner'}</TableCell>
                      <TableCell align='center'>{'Language'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((slider) => (
                        <TableRow key={slider.id}>
                          <TableCell
                            align='center'
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            {slider?.imageUrl && (
                              <Avatar
                                onClick={() =>
                                  handleOpenLightBox(slider?.imageUrl)
                                }
                                alt={slider?.imageUrl}
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(slider?.imageUrl, assetUrl)}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align='center'>
                            {slider?.position}
                          </TableCell>
                          <TableCell align='center'>
                            {slider?.actionType}
                          </TableCell>
                          <TableCell align='center'>
                            {(() => {
                              const placement = getBannerPlacement(slider);
                              const color =
                                placement === 'homeBottom'
                                  ? 'success'
                                  : placement === 'advertise'
                                  ? 'warning'
                                  : placement === ''
                                  ? 'info'
                                  : 'primary';
                              return (
                                <Label
                                  variant='ghost'
                                  color={color}
                                >
                                  {bannerPlacementLabel(slider)}
                                </Label>
                              );
                            })()}
                          </TableCell>
                          <TableCell align='center'>
                            {slider?.language}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={slider.activeState ? 'success' : 'error'}
                            >
                              {slider.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(slider)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(
                  Math.max(0, Math.ceil(totalRecords / limit) - 1),
                  currentPage
                )}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <SliderFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>

        {openLightBox && (
          <Box
            sx={{ pt: 'env(safe-area-inset-top) !important' }}
            id='myModal'
            className='modal'
          >
            <span className='close cursor'>
              <CloseIcon
                fontSize='large'
                onClick={handleCloseLightBox}
                sx={{ mt: 'env(safe-area-inset-top) !important' }}
              />
            </span>
            <div
              className={`modal-content ${
                openLightBox ? 'display-block ' : 'display-none'
              }`}
            >
              {imageState && (
                <div className='vertical-center-div'>
                  <div
                    ref={boxRef}
                    className={`mySlides ${
                      openLightBox ? 'display-block ' : 'display-none'
                    }`}
                  >
                    <img
                      id={imageState}
                      src={getAssetURl(imageState, assetUrl)}
                      width='100%'
                      height='100%'
                      crossOrigin='anonymous'
                      alt={imageState}
                    />
                  </div>
                </div>
              )}
            </div>
          </Box>
        )}
      </Page>
    </>
  );
};

export default Index;
